// src/useScrollDepthTracking.js
//
// Fires a `scroll_depth` GA4 event once per threshold (25/50/75/100) per
// page. Resets the fired-thresholds set whenever the route changes, so
// depth is tracked per-page, not just once for the whole session.
//
// Usage: call this once near the top of App.js (inside <Router>, since it
// needs useLocation). No JSX returned — it's a side-effect-only hook.

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackScrollDepth } from "./utils/analytics";

const THRESHOLDS = [25, 50, 75, 100];

export default function useScrollDepthTracking() {
  const location = useLocation();
  const firedRef = useRef(new Set());

  useEffect(() => {
    // New page — reset which thresholds have already fired.
    firedRef.current = new Set();

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const percent = Math.min(100, Math.round((scrollTop / docHeight) * 100));

      for (const threshold of THRESHOLDS) {
        if (percent >= threshold && !firedRef.current.has(threshold)) {
          firedRef.current.add(threshold);
          trackScrollDepth(threshold, location.pathname);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    // Check once on mount too, in case the page is short enough that the
    // user starts already "past" a threshold (e.g. 100% with no scrolling).
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);
}
